/**
 * TransportLayer — the video transport + gesture surface for one capsule page.
 *
 *  - Center transport cluster: [ -5 ] [ Play/Pause ] [ +5 ] — circular
 *    frosted-glass buttons (blurred dark tint, no stroke, white glyphs). The
 *    center icon is the Lottie play/pause morph; the ±5 buttons are circular
 *    arrows with the increment inside, sharing the exact same seek path as
 *    the side double-taps.
 *  - Three vertical tap zones: the MIDDLE third toggles play/pause AND
 *    reveals the cluster in one tap (no double-tap exists there, so it fires
 *    immediately); the LEFT/RIGHT thirds reveal the cluster on a single tap
 *    and start the cumulative ±5 seek on a double tap.
 *  - Double tap on the LEFT/RIGHT half starts a YouTube-style cumulative
 *    seek: ±5 on the double tap, then every following quick tap on the SAME
 *    side adds another 5, and the feedback chip shows the running TOTAL
 *    (+5 → +10 → +15 …). Taps share the exact same seek path as the buttons
 *    (doSeek → onSeek) — no separate seek logic. Burst taps never toggle the
 *    controls; only a tap outside a burst reveals them.
 *  - Press-and-hold (~500ms) runs the video at 1.5x with a persistent
 *    "1.5× Speed" indicator; release restores the previous rate.
 *  - Vertical drag (or a long press that starts moving) cancels tap/long-press
 *    recognition and falls through to the pager scroll — this layer never
 *    calls preventDefault on down/move, so native touch scrolling survives.
 *
 * The layer is a full-page, box-none overlay placed BELOW the capsule chrome
 * (z 10 vs chrome z 20): interactive chrome always wins; taps that fall
 * through the chrome reach the gesture surface here.
 */
import {useCallback, useEffect, useRef, useState} from 'react'
import {Animated, Pressable, StyleSheet, Text, View} from 'react-native'
import {BlurView} from 'expo-blur'
import {Feather} from '@expo/vector-icons'
import {useLingui} from '@lingui/react/macro'

import {useHaptics} from '#/lib/haptics'
import {IS_WEB} from '#/env'
import {
  BURST_WINDOW_MS,
  DOUBLE_TAP_WINDOW_MS,
  LONG_PRESS_MS,
} from '../pagerPolicy'
import {
  PlayPauseIcon,
  type PlayPauseIconHandle,
} from './PlayPauseIcon'
import {SeekArrow} from './SeekArrow'

const HIDE_DELAY_MS = 2500
const MOVE_SLOP_PX = 10
const CHIP_LIFE_MS = 1150

/** Seek deltas — buttons AND side double-tap bursts share ±5. */
export type SeekDelta = 5 | -5

type Side = 'left' | 'middle' | 'right'
/** Direction a seek chip points: 'back' ← amount, amount → 'fwd'. */
type SeekDir = 'back' | 'fwd'

type FeedbackChip = {
  id: number
  label: string
  side: Side
  dir?: SeekDir
}

type Burst = {
  side: Side
  /** Total accumulated seek of the current burst (displayed, in seconds). */
  total: number
  lastAt: number
}

function FeedbackChip({
  label,
  side,
  dir,
  center,
  onDone,
}: {
  label: string
  side: Side
  dir?: SeekDir
  center?: boolean
  onDone: () => void
}) {
  const opacity = useRef(new Animated.Value(0)).current
  const scale = useRef(new Animated.Value(0.7)).current
  useEffect(() => {
    Animated.parallel([
      Animated.timing(opacity, {
        toValue: 1,
        duration: 120,
        useNativeDriver: true,
      }),
      Animated.spring(scale, {
        toValue: 1,
        friction: 6,
        tension: 140,
        useNativeDriver: true,
      }),
    ]).start()
    const t = setTimeout(() => {
      Animated.timing(opacity, {
        toValue: 0,
        duration: 200,
        useNativeDriver: true,
      }).start(({finished}) => {
        if (finished) onDone()
      })
    }, CHIP_LIFE_MS)
    return () => clearTimeout(t)
  }, [opacity, scale, onDone])

  return (
    <Animated.View
      pointerEvents="none"
      style={[
        styles.chip,
        center || side === 'middle'
          ? {alignSelf: 'center'}
          : side === 'right'
            ? {alignSelf: 'flex-end', marginRight: 28}
            : {alignSelf: 'flex-start', marginLeft: 28},
        {opacity, transform: [{scale}]},
      ]}>
      {/* Directional arrow on the seek side — the ANIMATED seek-arrow badge
          (the arrow Lottie), replayed from frame 0 on every seek click: back
          chips lead with '← −15', forward chips trail '+15 →'. The centered
          1.5× indicator carries no arrow. Just number + arrow — no pill. */}
      {dir === 'back' ? <SeekArrow dir="back" /> : null}
      <Text style={styles.chipText}>{label}</Text>
      {dir === 'fwd' ? <SeekArrow dir="fwd" /> : null}
    </Animated.View>
  )
}

export function TransportLayer({
  videoPaused,
  onTogglePause,
  onSeek,
  onLongPressChange,
}: {
  videoPaused: boolean
  onTogglePause: () => void
  onSeek: (delta: SeekDelta) => void
  onLongPressChange: (active: boolean) => void
}) {
  const {t: l} = useLingui()
  const playHaptic = useHaptics()
  const [visible, setVisible] = useState(false)
  const [chips, setChips] = useState<FeedbackChip[]>([])
  const [rateActive, setRateActive] = useState(false)
  const fade = useRef(new Animated.Value(1)).current
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const burstChipIdRef = useRef<number | null>(null)
  // The single burst bookkeeper shared by the ±5 buttons and the side-tap
  // recognizers: one cumulative seek total per side, one replacing chip.
  const burstRef = useRef<Burst | null>(null)
  const iconRef = useRef<PlayPauseIconHandle>(null)
  // Tracks the paused-state the center icon's in-flight morph is heading to.
  // An external videoPaused change that MATCHES it is the result of our own
  // press (the animation already covers it); anything else snaps the icon.
  const animTargetRef = useRef<boolean | null>(null)

  const cbRef = useRef({videoPaused, onTogglePause, onSeek, onLongPressChange})
  useEffect(() => {
    cbRef.current = {videoPaused, onTogglePause, onSeek, onLongPressChange}
  })

  const clearHideTimer = useCallback(() => {
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current)
      hideTimerRef.current = null
    }
  }, [])

  const hide = useCallback(() => {
    clearHideTimer()
    Animated.timing(fade, {
      toValue: 0,
      duration: 260,
      useNativeDriver: true,
    }).start(({finished}) => {
      if (finished) setVisible(false)
    })
  }, [clearHideTimer, fade])

  const scheduleHide = useCallback(() => {
    clearHideTimer()
    if (cbRef.current.videoPaused) return // paused → keep the Play control
    hideTimerRef.current = setTimeout(() => hide(), HIDE_DELAY_MS)
  }, [clearHideTimer, hide])

  const reveal = useCallback(() => {
    setVisible(true)
    Animated.timing(fade, {
      toValue: 1,
      duration: 160,
      useNativeDriver: true,
    }).start()
  }, [fade])

  const bump = useCallback(() => {
    // Interaction (tap/seek/button) → reveal + reset the hide timer.
    reveal()
    scheduleHide()
  }, [reveal, scheduleHide])

  // Paused ⇒ the resume control must never disappear; playing ⇒ hide after
  // the idle window when the cluster is up.
  useEffect(() => {
    if (videoPaused) {
      clearHideTimer()
      reveal()
    } else if (visible) {
      scheduleHide()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoPaused])

  // External play-state changes drive the center icon. A change that matches
  // the in-flight morph target is our own press — the animation already
  // landed there — otherwise snap the icon to the new state.
  useEffect(() => {
    if (videoPaused === animTargetRef.current) {
      animTargetRef.current = null
      return
    }
    iconRef.current?.sync(videoPaused)

  }, [videoPaused])

  // Cleanup timers on unmount (pager navigation, X-close).
  useEffect(() => clearHideTimer, [clearHideTimer])

  const addChip = useCallback(
    (label: string, side: Side, isBurst = false, dir?: SeekDir) => {
      const id = Date.now() + Math.random()
      setChips(prev => {
        let next = prev
        if (isBurst && burstChipIdRef.current != null) {
          // The burst shows ONE chip that keeps updating to the running total.
          next = next.filter(c => c.id !== burstChipIdRef.current)
        }
        if (isBurst) burstChipIdRef.current = id
        return [...next.slice(-2), {id, label, side, dir}]
      })
    },
    [],
  )

  const removeChip = useCallback((id: number) => {
    if (burstChipIdRef.current === id) burstChipIdRef.current = null
    setChips(prev => prev.filter(c => c.id !== id))
  }, [])

  // ── Shared actions — buttons AND side taps go through these ───────────────
  // Buttons (reveal=true) reset the visibility timer; burst taps (reveal=false)
  // never toggle the controls — the chips are the only feedback.
  const doSeek = useCallback(
    (delta: SeekDelta, side: Side, reveal = true, burstTotal?: number) => {
      const label =
        burstTotal != null
          ? `${delta > 0 ? '+' : '-'}${burstTotal}`
          : `${delta > 0 ? '+' : ''}${delta}`
      addChip(label, side, burstTotal != null, delta < 0 ? 'back' : 'fwd')
      if (reveal) bump()
      playHaptic('Light')
      cbRef.current.onSeek(delta)
    },
    [addChip, bump, playHaptic],
  )

  // Buttons AND side taps run through ONE cumulative-burst seeker (the same
  // code path, never a fork): a seek on a side continues the running total
  // (+5 → +10 → …) while the clicks stay inside the burst window and each
  // click redraws the single chip; an idle gap or a side switch restarts at
  // ±5. Every click still seeks exactly one ±5 step. Button clicks reveal
  // the transport; side taps never toggle it.
  const doSideSeek = useCallback(
    (side: 'left' | 'right', reveal: boolean) => {
      const now = Date.now()
      const prev = burstRef.current
      burstRef.current =
        prev && prev.side === side && now - prev.lastAt <= BURST_WINDOW_MS
          ? {side, total: prev.total + 5, lastAt: now}
          : {side, total: 5, lastAt: now}
      doSeek(side === 'left' ? -5 : 5, side, reveal, burstRef.current.total)
    },
    [doSeek],
  )

  const doTogglePause = useCallback(() => {
    const nextPaused = !cbRef.current.videoPaused
    // Morph the icon to the state the press is switching the video TO.
    animTargetRef.current = nextPaused
    iconRef.current?.animateTo(nextPaused)
    bump()
    cbRef.current.onTogglePause()
  }, [bump])

  const doLongPressChange = useCallback(
    (active: boolean) => {
      cbRef.current.onLongPressChange(active)
      setRateActive(active)
      if (active) playHaptic('Light')
      // While the speed-up is held, the 1.5× indicator is the only feedback —
      // the transport cluster would read as misleading play/pause controls.
      // On release the paused-state rule applies again (resume control stays).
      if (active) hide()
      else if (cbRef.current.videoPaused) reveal()
    },
    [hide, reveal, playHaptic],
  )

  // ── Web: pointer-event recognition ─────────────────────────────────────────
  const surfaceRef = useRef<View>(null)
  useEffect(() => {
    if (!IS_WEB) return
    const el = (surfaceRef.current as unknown as HTMLElement | null) ?? null
    if (!el) return

    let destroyed = false
    let downX = 0
    let downY = 0
    let moved = false
    let holdTimer: ReturnType<typeof setTimeout> | null = null
    let longActive = false
    let singleTimer: ReturnType<typeof setTimeout> | null = null
    let tapCount = 0
    let lastUpAt = 0

    const clearTimers = () => {
      if (holdTimer) {
        clearTimeout(holdTimer)
        holdTimer = null
      }
      if (singleTimer) {
        clearTimeout(singleTimer)
        singleTimer = null
      }
    }

    const cancelGesture = () => {
      clearTimers()
      tapCount = 0
      burstRef.current = null
      if (longActive) {
        longActive = false
        doLongPressChange(false)
      }
    }

    const onPointerDown = (e: PointerEvent) => {
      if (destroyed) return
      // A second tap inside the double-tap window cancels the first tap's
      // pending single-tap reveal — but must NOT reset tapCount, or the
      // double-tap branch in onPointerUp could never fire.
      if (singleTimer) {
        clearTimeout(singleTimer)
        singleTimer = null
      }
      if (holdTimer) {
        clearTimeout(holdTimer)
        holdTimer = null
      }
      moved = false
      downX = e.clientX
      downY = e.clientY
      holdTimer = setTimeout(() => {
        if (!moved && !destroyed) {
          longActive = true
          doLongPressChange(true)
        }
      }, LONG_PRESS_MS)
    }

    const onPointerMove = (e: PointerEvent) => {
      if (destroyed || moved) return
      if (Math.hypot(e.clientX - downX, e.clientY - downY) > MOVE_SLOP_PX) {
        moved = true
        clearTimers()
        tapCount = 0
        burstRef.current = null
        if (longActive) {
          longActive = false
          doLongPressChange(false)
        }
      }
    }

    const sideOf = (x: number): Side => {
      const rect = el.getBoundingClientRect()
      const rel = x - rect.left
      const w = rect.width
      if (rel < w / 3) return 'left'
      if (rel > (2 * w) / 3) return 'right'
      return 'middle'
    }

    const onPointerUp = (e: PointerEvent) => {
      if (destroyed) return
      clearTimers()
      if (moved || longActive) {
        // A drag or a held release is not a tap — it must not count toward
        // the next double-tap and cancels any seek burst in progress.
        tapCount = 0
        burstRef.current = null
        if (longActive) {
          longActive = false
          doLongPressChange(false)
        }
        moved = false
        return
      }
      const now = Date.now()
      const side = sideOf(e.clientX)

      // Middle third: one tap toggles play/pause AND reveals the controls.
      // No double-tap exists there, so it fires immediately and ends any
      // pending side-tap reveal or seek burst.
      if (side === 'middle') {
        if (singleTimer) {
          clearTimeout(singleTimer)
          singleTimer = null
        }
        tapCount = 0
        burstRef.current = null
        doTogglePause()
        return
      }

      // A tap while a seek burst is still live continues it (buttons and side
      // taps share the one burst) — same side accumulates, a switch resets.
      if (burstRef.current && now - burstRef.current.lastAt <= BURST_WINDOW_MS) {
        tapCount = 0
        doSideSeek(side, false)
        return
      }
      burstRef.current = null

      if (tapCount >= 1 && now - lastUpAt <= DOUBLE_TAP_WINDOW_MS) {
        // Second tap → double tap: seek starts the burst.
        tapCount = 0
        doSideSeek(side, false)
        return
      }
      tapCount = 1
      lastUpAt = now
      singleTimer = setTimeout(() => {
        singleTimer = null
        tapCount = 0
        if (!destroyed) {
          // Single tap → reveal temporary controls.
          bump()
        }
      }, DOUBLE_TAP_WINDOW_MS)
    }

    const onPointerCancel = () => cancelGesture()

    el.addEventListener('pointerdown', onPointerDown)
    el.addEventListener('pointermove', onPointerMove)
    el.addEventListener('pointerup', onPointerUp)
    el.addEventListener('pointercancel', onPointerCancel)
    return () => {
      destroyed = true
      clearTimers()
      el.removeEventListener('pointerdown', onPointerDown)
      el.removeEventListener('pointermove', onPointerMove)
      el.removeEventListener('pointerup', onPointerUp)
      el.removeEventListener('pointercancel', onPointerCancel)
    }
  }, [bump, doLongPressChange, doSideSeek, doTogglePause])

  // ── Native: Pressable recognition (mirrors the web pointer logic) ─────────
  const nativeState = useRef<{
    surfaceWidth: number
    lastTapAt: number
    lastTapSide: Side
    pendingBump: ReturnType<typeof setTimeout> | null
    lastLongPressAt: number
  }>({
    surfaceWidth: 300,
    lastTapAt: 0,
    lastTapSide: 'left',
    pendingBump: null,
    lastLongPressAt: 0,
  })

  useEffect(() => {
    const s = nativeState.current
    return () => {
      if (s.pendingBump) clearTimeout(s.pendingBump)
    }
  }, [])

  const onSurfaceTap = useCallback(() => {
    const s = nativeState.current
    const now = Date.now()
    const side = s.lastTapSide

    // Middle third: one tap toggles play/pause + reveals the controls —
    // unless the tap is the release of a long-press (1.5x speed), which
    // must not pause the video.
    if (side === 'middle') {
      if (now - s.lastLongPressAt < 700) return
      burstRef.current = null
      s.lastTapAt = 0
      if (s.pendingBump) {
        clearTimeout(s.pendingBump)
        s.pendingBump = null
      }
      doTogglePause()
      return
    }

    // Continuing an active burst (YouTube-style cumulative side seek).
    if (burstRef.current && now - burstRef.current.lastAt <= BURST_WINDOW_MS) {
      if (s.pendingBump) {
        clearTimeout(s.pendingBump)
        s.pendingBump = null
      }
      doSideSeek(side, false)
      return
    }
    burstRef.current = null

    if (now - s.lastTapAt <= DOUBLE_TAP_WINDOW_MS) {
      // Second tap → cancel the pending single reveal and seek the side.
      if (s.pendingBump) {
        clearTimeout(s.pendingBump)
        s.pendingBump = null
      }
      s.lastTapAt = 0
      doSideSeek(side, false)
      return
    }
    s.lastTapAt = now
    // Delay the reveal past the double-tap window so a double tap never
    // shows the controls twice.
    s.pendingBump = setTimeout(() => {
      s.pendingBump = null
      bump()
    }, DOUBLE_TAP_WINDOW_MS)
  }, [bump, doSideSeek, doTogglePause])

  return (
    <View
      pointerEvents="box-none"
      style={[StyleSheet.absoluteFill, {zIndex: 10}]}>
      {/* Gesture surface — fills the page under the chrome. */}
      {IS_WEB ? (
        <View ref={surfaceRef} style={[StyleSheet.absoluteFill]} />
      ) : (
        <Pressable
          accessibilityLabel={l`Video gesture surface`}
          accessibilityHint={l`Tap to show controls, double-tap the sides to seek, hold for 1.5x speed`}
          onLayout={e => {
            nativeState.current.surfaceWidth = e.nativeEvent.layout.width
          }}
          onPressIn={e => {
            const x = e.nativeEvent.locationX
            const w = nativeState.current.surfaceWidth
            nativeState.current.lastTapSide =
              x < w / 3 ? 'left' : x > (2 * w) / 3 ? 'right' : 'middle'
          }}
          onPress={onSurfaceTap}
          onLongPress={() => {
            nativeState.current.lastLongPressAt = Date.now()
            doLongPressChange(true)
          }}
          onPressOut={() => doLongPressChange(false)}
          delayLongPress={LONG_PRESS_MS}
          style={[StyleSheet.absoluteFill]}
        />
      )}

      {/* Feedback chips (side seeks + 1.5x rate). */}
      <View
        pointerEvents="none"
        style={[StyleSheet.absoluteFill, {justifyContent: 'center'}]}>
        {rateActive ? (
          <FeedbackChip
            label="1.5× Speed"
            side="left"
            center
            onDone={() => {}}
          />
        ) : null}
        {chips.map(chip => (
          <FeedbackChip
            key={chip.id}
            label={chip.label}
            side={chip.side}
            dir={chip.dir}
            onDone={() => removeChip(chip.id)}
          />
        ))}
      </View>

      {/* Transport cluster. */}
      {visible && (
        <Animated.View
          pointerEvents="auto"
          style={[styles.cluster, {opacity: fade}]}>
          <View style={styles.clusterRow}>
            <Pressable
              accessibilityRole="button"
              accessibilityLabel={l`Seek backward 5 seconds`}
              accessibilityHint={l`Rewinds the video by 5 seconds`}
              onPress={() => doSideSeek('left', true)}
              style={({pressed}) => [
                styles.transportBtn,
                pressed && styles.pressed,
              ]}>
              <BlurView
                intensity={26}
                tint="dark"
                style={StyleSheet.absoluteFill}
              />
              <View style={styles.seekGlyphWrap}>
                <Feather name="rotate-ccw" size={22} color="#fff" />
                <Text style={styles.seekGlyphNum}>5</Text>
              </View>
            </Pressable>

            <Pressable
              accessibilityRole="button"
              accessibilityLabel={videoPaused ? l`Play video` : l`Pause video`}
              accessibilityHint={
                videoPaused ? l`Resumes the video` : l`Pauses the video`
              }
              onPress={doTogglePause}
              style={({pressed}) => [
                styles.transportBtn,
                styles.transportBtnMain,
                pressed && styles.pressed,
              ]}>
              <BlurView
                intensity={32}
                tint="dark"
                style={StyleSheet.absoluteFill}
              />
              <PlayPauseIcon ref={iconRef} size={36} />
            </Pressable>

            <Pressable
              accessibilityRole="button"
              accessibilityLabel={l`Seek forward 5 seconds`}
              accessibilityHint={l`Advances the video by 5 seconds`}
              onPress={() => doSideSeek('right', true)}
              style={({pressed}) => [
                styles.transportBtn,
                pressed && styles.pressed,
              ]}>
              <BlurView
                intensity={26}
                tint="dark"
                style={StyleSheet.absoluteFill}
              />
              <View style={styles.seekGlyphWrap}>
                <Feather name="rotate-cw" size={22} color="#fff" />
                <Text style={styles.seekGlyphNum}>5</Text>
              </View>
            </Pressable>
          </View>
        </Animated.View>
      )}
    </View>
  )
}

const styles = StyleSheet.create({
  chip: {
    // Just the number + the animated arrow — deliberately no background or
    // border, so the feedback never reads as a button.
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    marginVertical: 4,
  },
  chipText: {
    color: '#fff',
    fontSize: 15,
    fontWeight: '800',
    textShadowColor: 'rgba(0,0,0,0.9)',
    textShadowOffset: {width: 0, height: 2},
    textShadowRadius: 6,
  },
  cluster: {
    position: 'absolute',
    left: 0,
    right: 0,
    top: '38%',
    alignItems: 'center',
  },
  clusterRow: {flexDirection: 'row', alignItems: 'center', gap: 14},
  transportBtn: {
    width: 44,
    height: 44,
    borderRadius: 22,
    overflow: 'hidden',
    alignItems: 'center',
    justifyContent: 'center',
    // Translucent fill so the circle reads over bright video; the BlurView
    // behind it supplies the frosted-glass look. Deliberately NO border
    // ("premium blur without stroke").
    backgroundColor: 'rgba(255,255,255,0.10)',
  },
  transportBtnMain: {
    width: 60,
    height: 60,
    borderRadius: 30,
    backgroundColor: 'rgba(255,255,255,0.14)',
  },
  pressed: {
    opacity: 0.8,
    transform: [{scale: 0.95}],
  },
  seekGlyphWrap: {
    width: '100%',
    height: '100%',
    alignItems: 'center',
    justifyContent: 'center',
  },
  seekGlyphNum: {
    position: 'absolute',
    color: '#fff',
    fontSize: 11,
    fontWeight: '800',
    marginTop: 1,
  },
})